import { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Plus, Trash2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { FormattedMessage, useIntl } from "react-intl";
function padZero(num) {
  return num.toString().padStart(2, "0");
}

function formatDuration(hhmmss) {
  if (!hhmmss) return "—";
  const [hh, mm, ss] = hhmmss.split(":").map(Number);
  if (isNaN(hh) || isNaN(mm)) return "—";
  const totalMins = hh * 60 + mm;
  if (totalMins === 0) return "—";
  if (totalMins < 60) return `${totalMins}m`;
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  return `${hours}h ${mins}m`;
}

function TimePicker({ label, hours, minutes, setHours, setMinutes }) {
  const intl = useIntl();
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex space-x-2">
        <Input
          type="number"
          min={0}
          max={23}
          placeholder={intl.formatMessage({ id: "TimePicker.Hours" })}
          value={hours}
          onChange={(e) => {
            let val = e.target.value;
            if (val === "") val = "";
            else if (val < 0) val = 0;
            else if (val > 23) val = 23;
            setHours(val);
          }}
          className="w-20"
        />
        <Input
          type="number"
          min={0}
          max={59}
          placeholder={intl.formatMessage({ id: "TimePicker.Minutes" })}
          value={minutes}
          onChange={(e) => {
            let val = e.target.value;
            if (val === "") val = "";
            else if (val < 0) val = 0;
            else if (val > 59) val = 59;
            setMinutes(val);
          }}
          className="w-20"
        />
      </div>
    </div>
  );
}

function assembleTimeString(hours, minutes) {
  const h = parseInt(hours, 10) || 0;
  const m = parseInt(minutes, 10) || 0;
  return `${padZero(h)}:${padZero(m)}:00`;
}

function TimeTab({ fieldStatus, addOrUpdateField, removeField }) {
  const intl = useIntl();
  // Set default date to today’s YYYY-MM-DD
  const todayDate = new Date().toISOString().split("T")[0];
  const [date, setDate] = useState(todayDate);
  const [travelHours, setTravelHours] = useState("");
  const [travelMinutes, setTravelMinutes] = useState("");
  const [jobHours, setJobHours] = useState("");
  const [jobMinutes, setJobMinutes] = useState("");
  const onAddRecord = () => {
    if (!date) {
      alert(intl.formatMessage({ id: "TimeTab.Alert.SelectDate" }));
      return;
    }
    // Validate times, both zero means no input
    if (
      travelHours === "" &&
      travelMinutes === "" &&
      jobHours === "" &&
      jobMinutes === ""
    ) {
      alert(intl.formatMessage({ id: "TimeTab.Alert.EnterTime" }));
      return;
    }

    const record = {
      id: 0, // set to 0 for new; actual id from backend if exists
      date,
      totalTravelTime: assembleTimeString(travelHours, travelMinutes),
      totalJobTime: assembleTimeString(jobHours, jobMinutes),
      // Include appointmentId in parent or context when calling this
    };

    addOrUpdateField(record);

    // Reset inputs after adding
    setDate(todayDate);
    setTravelHours("");
    setTravelMinutes("");
    setJobHours("");
    setJobMinutes("");
  };

  return (
    <>
      <Card>
        <CardHeader className="!p-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <FormattedMessage id="TimeTab.AddRecord" />
          </CardTitle>
        </CardHeader>
        <CardContent className="!p-4 !pt-0 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 items-end">
            <div>
              <Label>
                <FormattedMessage id="TimeTab.Date" />
              </Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                max={new Date().toISOString().split("T")[0]}
              />
            </div>

            <TimePicker
              label={intl.formatMessage({ id: "TimeTab.TravelTime" })}
              hours={travelHours}
              minutes={travelMinutes}
              setHours={setTravelHours}
              setMinutes={setTravelMinutes}
            />
            <TimePicker
              label={intl.formatMessage({ id: "TimeTab.JobTime" })}
              hours={jobHours}
              minutes={jobMinutes}
              setHours={setJobHours}
              setMinutes={setJobMinutes}
            />
          </div>

          <Button onClick={onAddRecord} className="w-full">
            <Plus className="mr-2" /> <FormattedMessage id="TimeTab.Add" />
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="!p-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <FormattedMessage id="TimeTab.Records" />
          </CardTitle>
        </CardHeader>
        <CardContent className="!p-4 !pt-0">
          {fieldStatus?.length === 0 ? (
            <p className="text-center text-gray-500 py-4">
              <FormattedMessage id="TimeTab.NoRecords" />
            </p>
          ) : (
            <table className="min-w-full table-auto border border-gray-200">
              <thead>
                <tr className="bg-gray-100 dark:bg-muted">
                  <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                    <FormattedMessage id="TimeTab.Date" />
                  </th>
                  <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                    <FormattedMessage id="TimeTab.TravelTime" />
                  </th>
                  <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                    <FormattedMessage id="TimeTab.JobTime" />
                  </th>
                  <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-center text-sm text-gray-900 dark:text-gray-100">
                    <FormattedMessage id="TimeTab.Actions" />
                  </th>
                </tr>
              </thead>

              <tbody>
                {fieldStatus?.map((record) => (
                  <tr
                    key={record.id || record.date}
                    className="hover:bg-gray-50"
                  >
                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2">
                      {record.date}
                    </td>
                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2">
                      {formatDuration(record.totalTravelTime)}
                    </td>
                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2">
                      {formatDuration(record.totalJobTime)}
                    </td>
                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-center">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => removeField(record.id)}
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </>
  );
}

export default TimeTab;
